//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import pluralize from "pluralize";
// defrag daily results card imports
import { IState, IProps, IContainer } from "./TailoredDefragSummaryCard.d";
import * as s from "./TailoredDefragSummaryCard.styles";
import { TailoredDefragSummaryAttendeeStats } from "./tailored-defrag-summary-attendee-stats";
import { TailoredDefragSummaryEventsMovedList } from "./tailored-defrag-summary-events-moved-list";
import { TailoredDefragSummarySurveyDialog } from "./tailored-defrag-summary-survey-dialog";
import {
  TailoredDefragSummarySurveyActions,
  TailoredDefragSummaryCardAction,
} from "./tailored-defrag-summary-actions";
// other components
import { H2 } from "@ui";
import { asCard } from "@components/card";
import { CardTopLine } from "@components/card-top-line";
import { ChromeStoreReviewDialog } from "@components/chrome-store-review-dialog";
// util
import { track, TrackingEvents } from "@util/analytics.util";
import { fromGlobalId } from "@util/graphql.util";
// state
import { snackBarOpen } from "@actions/snack-bar.actions";
import { IReduxState } from "@state/reducers/root.reducer";
import { lastDefragCardTimestamp, chromeWebstoreReviewResponseDate } from "@state/local-storage";
import { recordDefragResponse, IDefragResponseKey } from "@mutations";

//////////////////
// COMPONENT
//////////////////
class TailoredDefragSummaryCardComponent extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      showChromeStoreReviewDialog: false,
      showDefragResponseSurvey: false,
      selectedFeedback: null,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-

  private sendSurveyResults = (
    feedback: ISchema.DefragFeedback,
    preDefinedReasons: string[] = [],
    otherReason: string = "",
  ) => {
    const { org } = this.props;
    const tailoredDefragCardSummary = org.tailoredDefragCardSummary;

    // not possible because of render check but helps type system know
    if (!tailoredDefragCardSummary || tailoredDefragCardSummary.__typename === "GraphEntityError") {
      return;
    }

    // prepare response
    const eventsMoved = tailoredDefragCardSummary.yourEventMoves;
    const responseKeys: IDefragResponseKey[] = eventsMoved.map((eventMoved) => {
      return {
        externalEventId: eventMoved.event.eventKey.externalEventId,
        defragMillis: tailoredDefragCardSummary.defragTime,
      };
    });

    // send response
    const environment = (this.props.relay as any).environment;
    let successMsg = "Thanks for your feedback";
    if (feedback !== "LooksGood") {
      successMsg = "Sorry to hear that, we will reach out to learn more.";
    }
    const orgId = fromGlobalId(org.id).id;
    recordDefragResponse(
      environment,
      { responseKeys, orgId, feedback, preDefinedReasons, otherReason },
      () => this.props.snackBarOpen(successMsg),
      () => this.props.snackBarOpen("Sorry, an error occurred recording your response."),
    );

    // analytics
    track(TrackingEvents.DEFRAG.SEND_FEEDBACK, {
      orgId,
      feedback,
    });
  };

  private onClickSurveyResponse = (feedback: ISchema.DefragFeedback) => {
    this.setState({ selectedFeedback: feedback });

    // send survey results first time
    this.sendSurveyResults(feedback);
    if (feedback === "LooksGood") {
      if (!chromeWebstoreReviewResponseDate.get()) {
        this.setState({ showChromeStoreReviewDialog: true });
      }
    } else {
      this.setState({ showDefragResponseSurvey: true });
    }
  };

  private removeCard = () => {
    const { org } = this.props;
    const tailoredDefragCardSummary = org.tailoredDefragCardSummary;
    if (
      !tailoredDefragCardSummary ||
      tailoredDefragCardSummary.__typename === "GraphEntityError" ||
      !tailoredDefragCardSummary.defragTime
    ) {
      return;
    }

    // store our last timestamp
    lastDefragCardTimestamp.set(new Date().valueOf().toString());

    if (this.props.removeCard) {
      this.props.removeCard();
    }
  };

  private onSurveyClose = (
    sendFeedback: boolean,
    selectedReasons: string[],
    otherReasonChecked: boolean,
    otherReason: string,
    additionalComments: string,
  ) => {
    const { selectedFeedback } = this.state;
    if (sendFeedback && selectedFeedback) {
      const preDefinedReasons: string[] = [
        ...selectedReasons,
        ...(otherReasonChecked ? [otherReason] : []),
      ];
      this.sendSurveyResults(selectedFeedback, preDefinedReasons, additionalComments);
    }
    this.setState({ showDefragResponseSurvey: false });
  };

  private onChromeStoreReviewDialogClose = () => {
    this.setState({ showChromeStoreReviewDialog: false });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-

  private maybeRenderChromeStoreReviewDialog = () => {
    const { showChromeStoreReviewDialog } = this.state;
    if (!showChromeStoreReviewDialog) {
      return null;
    }

    return (
      <ChromeStoreReviewDialog open={true} onClose={() => this.onChromeStoreReviewDialogClose()} />
    );
  };

  public render() {
    const { tailoredDefragCardSummary } = this.props.org;
    if (!tailoredDefragCardSummary || tailoredDefragCardSummary.__typename === "GraphEntityError") {
      return null;
    }

    const eventsMoved = tailoredDefragCardSummary.yourEventMoves;

    let rooms: ISchema.IRoomList | null = null;
    if (tailoredDefragCardSummary.rooms.__typename !== "GraphEntityError") {
      rooms = tailoredDefragCardSummary.rooms;
    }

    return (
      <div style={s.container}>
        <div style={s.topLine}>
          <CardTopLine
            timeStamp={tailoredDefragCardSummary.defragTime}
            clear={this.removeCard}
            label="Assistant"
          />
        </div>
        <div style={s.headerBody}>
          <H2>
            Clockwise updated {eventsMoved.length} {pluralize("meeting", eventsMoved.length)} you're
            attending
          </H2>
          <TailoredDefragSummaryAttendeeStats
            tailoredDefragCardSummary={tailoredDefragCardSummary}
            org={this.props.org}
          />
        </div>
        <div>
          <TailoredDefragSummaryEventsMovedList eventsMoved={eventsMoved} rooms={rooms} />
          <TailoredDefragSummarySurveyActions
            selectedFeedback={this.state.selectedFeedback}
            onFeedbackSelect={this.onClickSurveyResponse}
          />
        </div>
        {this.state.showDefragResponseSurvey ? (
          <TailoredDefragSummarySurveyDialog
            open={this.state.showDefragResponseSurvey}
            onClose={this.onSurveyClose}
          />
        ) : (
          ""
        )}
        {this.maybeRenderChromeStoreReviewDialog()}
        <TailoredDefragSummaryCardAction onClick={this.removeCard} />
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(TailoredDefragSummaryCardComponent);

//////////////////
// RELAY
//////////////////
import { fragments } from "./TailoredDefragSummaryCard.gql";
export const TailoredDefragSummaryCard = createFragmentContainer<IContainer>(
  asCard<IContainer>(reduxContainer),
  fragments,
);
